import React from 'react';
import { fmtLabel, fmtTime12 } from '../utils/format';
import { API } from '../api';
import PosterLightbox from './PosterLightbox';
import { fmtWhen } from './EventsView';
import { canUserEnterComp, getMembershipRenewalStatus } from '../utils/membership';

/*
 * The front door.
 *
 * The app opened on a club dashboard: three columns of competitions, results
 * and standings, addressed to nobody in particular. But most people opening
 * it are players, and the thing they came to do — enter a competition, or find
 * out which rink they are on — was several taps deep.
 *
 * This is ordered by what is true for *you* today rather than by category:
 * the match you are about to play, then what you could enter, then how your
 * season is going. Club-wide views and admin are still there; they are now
 * somewhere you go rather than the first thing you meet.
 *
 * On a quiet day nothing renders empty. With no match on, the next fixture is
 * promoted into first place and entry moves up behind it — see the "quiet day"
 * board on the design canvas.
 */

const startOfDay = d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/* ------------------------------------------------------------------
   How far ahead the front page looks.

   A club puts its whole season in at the start of the year, so with no
   horizon "Open for entry" becomes a list of things happening in eight
   months' time with next Saturday somewhere in the middle of it. Two
   months is about as far ahead as anybody acts; past that a member
   reads the row, thinks "not yet", and learns to skim the section.

   Nothing is hidden, only folded: everything beyond the horizon sits
   behind a "more" underneath, and the competitions and events pages
   still list the lot.
   ------------------------------------------------------------------ */
export const HORIZON_MONTHS = 2;

/**
 * Is this near enough to be worth the front page?
 *
 * Undated counts as near. There is no date to say it is far off, a
 * competition with no date fixed is still open for entry now, and burying it
 * would hide the one kind of row a member can act on immediately.
 *
 * Anything already past counts as far: it cannot be acted on either, and the
 * front page is a list of things to do.
 */
export function isNear(date, today = startOfDay(new Date()), months = HORIZON_MONTHS) {
  if (!date) return true;
  const when = startOfDay(new Date(date));
  if (Number.isNaN(when.getTime())) return true;
  if (when < startOfDay(today)) return false;
  const horizon = startOfDay(today);
  horizon.setMonth(horizon.getMonth() + months);
  return when <= horizon;
}

function PlayerHomeView({ clubs, comps, currentUser, dashLeaderboard, isAdmin, myCompetitionIds, myRegistrations, needsMembershipAction, onOpenMyProfile, openComp, openMemberIdPage, recentMatches, selectedClubId, setView, upcomingMatches }) {
  /* Club events, on the same page as competitions because a member does not
     sort what is on into two categories in their head. Fetched here rather
     than passed down: nothing above this screen needs them, and the endpoint
     already returns only what a member should see. */
  const [events, setEvents] = React.useState([]);
  const [posters, setPosters] = React.useState([]);
  const [zoom, setZoom] = React.useState(null);
  // The event behind a poster tile, so the opened poster can carry its words.
  const [zoomEvent, setZoomEvent] = React.useState(null);
  /* Folded away by default, and only for this visit: somebody who opened
     the whole season yesterday still wants the short list tomorrow. */
  const [showAllEntry, setShowAllEntry] = React.useState(false);
  const [showAllEvents, setShowAllEvents] = React.useState(false);

  React.useEffect(() => {
    if (!selectedClubId) { setEvents([]); setPosters([]); return undefined; }
    let cancelled = false;
    fetch(`${API}/api/events?clubId=${selectedClubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setEvents(Array.isArray(d) ? d : []); })
      .catch(() => {});
    /* gallery=1 brings the 480px thumbnails and leaves the 4K images behind.
       This page would otherwise pull several megabytes to draw a row of
       pictures an inch wide. */
    fetch(`${API}/api/posters?clubId=${selectedClubId}&gallery=1`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setPosters(Array.isArray(d) ? d : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedClubId]);

  /* The poster advertising a thing, if there is one showing. Same test the
     club screen and the events page use: switched on, and not past its
     show-until date. One answer to "is this poster live?", everywhere. */
  const posterFor = React.useCallback((field, id) => {
    const today = new Date().toISOString().slice(0, 10);
    return posters.find(p => String(p[field]) === String(id)
      && p.active
      && (!p.showUntil || String(p.showUntil).slice(0, 10) >= today)
      && p.thumbData) || null;
  }, [posters]);

  /* A poster beside a row: small, and it opens. The row still says
     everything in words — the picture is a second way in, not the only one. */
  const PosterChip = ({ poster }) => (poster ? (
    <button type="button" className="home-poster-chip" onClick={() => setZoom(poster)}
      aria-label={`See the poster for ${poster.title}`}>
      <img src={poster.thumbData} alt="" />
    </button>
  ) : null);

  const initials = (currentUser && currentUser.name ? currentUser.name : '')
    .split(/s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

  const renewal = getMembershipRenewalStatus(currentUser);

  const dateish = v => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const club = clubs.find(c => String(c.id) === String(selectedClubId || (currentUser && currentUser.ClubId)));

  /* Whether the signed-in player is in a match. Team objects carry their
     Users, so this needs no extra request. */
  const playerIsIn = m => {
    if (!currentUser) return false;
    const home = m.HomeTeam ? m.HomeTeam.Users || [] : [];
    const away = m.AwayTeam ? m.AwayTeam.Users || [] : [];
    return home.some(u => u.id === currentUser.id) || away.some(u => u.id === currentUser.id);
  };

  const today = startOfDay(new Date());
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);

  /* How far ahead the front page looks.
   *
     A club puts its whole season in at the start of the year, so without a
     horizon "Open for entry" is a list of things happening in eight months'
     time with next Saturday somewhere in the middle of it. Two months is
     about as far ahead as anybody acts: past that a member reads the row,
     thinks "not yet", and learns to skim the section.
   *
     Nothing is hidden, only folded — everything past the horizon is behind
     a "more" underneath, and the competitions and events pages still list
     the lot. */
  const withinHorizon = d => isNear(d, today);

  const myUpcoming = (upcomingMatches || [])
    .filter(m => m.scheduledAt && playerIsIn(m))
    .map(m => ({ ...m, when: new Date(m.scheduledAt) }))
    .sort((a, b) => a.when - b.when);

  const onNow = myUpcoming.find(m => m.when >= today && m.when < tomorrow);
  const nextUp = myUpcoming.find(m => m.when >= tomorrow);
  const feature = onNow || nextUp;

  const myLastMatch = (recentMatches || []).find(playerIsIn);

  /* Competitions the player is already taking part in. Registering is not the
     only way in — an admin can add someone to a competition directly — so
     "am I in it" has to come from the draw as well as from myRegistrations.
     Without this, a competition you are playing in *today* is offered to you
     under "Open for entry". */
  const myCompIds = new Set([
    /* Three ways of being in a competition, and the home page has to know
       about all of them or it goes on offering you something you are already
       playing in: the club's player list for it, and the draw. (The third,
       an outstanding registration, is myRegistrations below.) */
    ...(myCompetitionIds || []),
    ...[...(upcomingMatches || []), ...(recentMatches || [])]
      .filter(playerIsIn)
      .map(m => m.compId),
  ].map(String));

  /* Open for entry: accepting registrations, not one you are already in, and
     not one that has already started — you cannot enter a competition that is
     part-played. */
  const openForEntry = (comps || []).filter(c => {
    if (c.registrationClosed) return false;
    if (c.status === 'in-progress' || c.status === 'reopened' || c.status === 'completed') return false;
    if (myRegistrations && myRegistrations[c.id]) return false;
    if (myCompIds.has(String(c.id))) return false;
    /* Only what this member could actually enter. This heading is a list of
       things to do, and a competition their membership shuts them out of is
       not one — it was shown with an explanation, but on the front page that
       is a row of "you cannot do this" above the rows they can.
       The competitions area still lists every competition the club is
       running; this narrowing is the home page's alone. */
    if (!canUserEnterComp(c, currentUser)) return false;
    return true;
  }).sort((a, b) => {
    /* Three tiers, because a plain date sort puts a stale 2025 draft above a
       competition starting next week. Still to come first, soonest at the
       top; then undated; then anything whose date has already passed. */
    const tier = c => (!c.startDate ? 1 : (new Date(c.startDate) >= today ? 0 : 2));
    const ta = tier(a);
    const tb = tier(b);
    if (ta !== tb) return ta - tb;
    if (ta === 1) return 0;
    const da = new Date(a.startDate) - new Date(b.startDate);
    return ta === 0 ? da : -da;
  });

  /* Split rather than sliced. The old .slice(0, 5) cut the list at an
     arbitrary five with no way to see the sixth; this shows everything
     inside the horizon and folds the rest. */
  const entryNear = openForEntry.filter(c => withinHorizon(c.startDate));
  const entryLater = openForEntry.filter(c => !withinHorizon(c.startDate));
  const shownEntry = showAllEntry ? openForEntry : entryNear;

  const eventDate = ev => ev.nextDate || ev.startDate;
  const eventsNear = events.filter(ev => withinHorizon(eventDate(ev)));
  const eventsLater = events.filter(ev => !withinHorizon(eventDate(ev)));
  const shownEvents = showAllEvents ? events : eventsNear;
  /* An event with a poster is shown as the poster. Somebody made it to say
     all of this better than a row of text can, and on a phone a grid of
     posters is what a member scrolls for. The rest stay as rows. */
  const posterEvents = shownEvents
    .map(ev => ({ ev, poster: posterFor('EventId', ev.id) }))
    .filter(x => x.poster);
  const textEvents = shownEvents.filter(ev => !posterFor('EventId', ev.id));

  const myEntered = (comps || [])
    .filter(c => c.status !== 'completed' && ((myRegistrations && myRegistrations[c.id]) || myCompIds.has(String(c.id))))
    .slice(0, 3);

  /* Where the player sits on the club ladder. Position is the index among
     players, not among players and teams mixed together. */
  const playerStandings = ((dashLeaderboard && dashLeaderboard.standings) || []).filter(s => s.type === 'player');
  const myIdx = currentUser ? playerStandings.findIndex(s => s.id === currentUser.id) : -1;
  const me = myIdx >= 0 ? playerStandings[myIdx] : null;

  const comingUp = (comps || [])
    .filter(c => c.startDate && new Date(c.startDate) >= today && c.status !== 'completed')
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .slice(0, 4);

  const ordinal = n => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  /* Is there anything else on this page? A heading that exists only to say
     "nothing" is worth its space only when the alternative is a blank screen.
     With a list of competitions right underneath it, "No matches scheduled
     for you" is a row of text telling you what you can already see. */
  const hasSomethingElse = openForEntry.length > 0 || events.length > 0
    || myEntered.length > 0 || !!me || comingUp.length > 0;

  const dayLabel = d => new Date(d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const opponentOf = m => {
    if (!currentUser) return null;
    const home = m.HomeTeam ? m.HomeTeam.Users || [] : [];
    const iAmHome = home.some(u => u.id === currentUser.id);
    const mine = iAmHome ? m.HomeTeam : m.AwayTeam;
    const theirs = iAmHome ? m.AwayTeam : m.HomeTeam;
    return { mine: mine && mine.name, theirs: theirs && theirs.name };
  };

  return (
    <div className="page-container">
      <div className="player-home">

        <div className="player-home-main">

          {/* ── 1. the match ─────────────────────────────────────────── */}
          {feature ? (() => {
            const o = opponentOf(feature);
            const isToday = feature === onNow;
            return (
              <>
                <span className="gs-section-label">{isToday ? 'On now' : 'Next up'}</span>
                <div className={`player-home-feature${isToday ? ' player-home-feature-today' : ''}`}>
                  <div className="player-home-when">
                    {isToday
                      ? <>{feature.time ? fmtTime12(feature.time) : 'Today'}{feature.rink ? ` · Rink ${feature.rink}` : ''}</>
                      : dayLabel(feature.when)}
                  </div>
                  <div className="player-home-who">
                    {o.mine} <span style={{ color: 'var(--gs-text-faint)' }}>v</span> {o.theirs || 'TBC'}
                  </div>
                  <div className="player-home-sub">
                    {feature.compName}
                    {feature.round ? ` · Round ${feature.round}` : ''}
                    {!isToday && feature.time ? ` · ${fmtTime12(feature.time)}` : ''}
                    {!isToday && feature.rink ? ` · Rink ${feature.rink}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--gs-space-3)', marginTop: 'var(--gs-space-4)', flexWrap: 'wrap' }}>
                    <button className="w3-button w3-round player-home-primary"
                      onClick={() => openComp(comps.find(c => c.id === feature.compId))}>
                      {isToday ? 'Enter score' : 'See the draw'}
                    </button>
                  </div>
                </div>
              </>
            );
          })() : !hasSomethingElse && (
            /* Only when the page would otherwise be empty. Somewhere to land
               beats a blank screen; a "nothing here" heading above a page full
               of things does not. */
            <>
              <span className="gs-section-label">Next up</span>
              <div className="player-home-feature">
                <div className="player-home-who" style={{ marginTop: 0 }}>No matches scheduled for you.</div>
                <div className="player-home-sub">Nothing open for entry just now.</div>
              </div>
            </>
          )}

          {/* ── 2. what you could enter ──────────────────────────────── */}
          {openForEntry.length > 0 && (
            <>
              <div className="player-home-rule"></div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gs-space-3)' }}>
                <span className="gs-section-label">Open for entry</span>
                {/* The count of what is on screen, not of what exists — a
                    badge saying 9 above three rows is a puzzle — and no
                    badge at all when everything is past the horizon, since
                    a grey 0 beside a heading reads as a fault. */}
                {shownEntry.length > 0 && (
                  <span style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-faint)' }}>{shownEntry.length}</span>
                )}
              </div>
              <div style={{ marginTop: 'var(--gs-space-3)' }}>
                {shownEntry.map(c => (
                    <div key={c.id} className="player-home-row">
                      <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--gs-text-base)', color: 'var(--gs-text)' }}>{c.name}</div>
                        <div className="player-home-meta">
                          {c.startDate ? dayLabel(c.startDate) : 'Date to be confirmed'}
                          {Number(c.entryFee) > 0 ? ` · $${Number(c.entryFee).toFixed(2)}` : ' · Free'}
                          {c.format ? ` · ${fmtLabel(c.format)}` : ''}
                        </div>
                      </div>
                      <PosterChip poster={posterFor('CompetitionId', c.id)} />
                      <button className="w3-button w3-round player-home-enter"
                        onClick={() => openComp(c)}>
                        Enter
                      </button>
                    </div>
                ))}
              </div>
              {entryLater.length > 0 && (
                <button type="button" className="player-home-more"
                  aria-expanded={showAllEntry}
                  onClick={() => setShowAllEntry(v => !v)}>
                  {showAllEntry
                    ? 'Show fewer'
                    : `${entryLater.length} more, later in the year`}
                </button>
              )}
            </>
          )}

          {events.length > 0 && (
            <>
              <div className="player-home-rule"></div>
              {/* Posters say what they are, so a heading and a count above a
                  grid of them is only clutter. Text-only rows still get one. */}
              {posterEvents.length === 0 && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gs-space-3)' }}>
                  <span className="gs-section-label">Also on at the club</span>
                  {shownEvents.length > 0 && (
                    <span style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-faint)' }}>{shownEvents.length}</span>
                  )}
                </div>
              )}
              {posterEvents.length > 0 && (
                <div className="home-poster-tiles">
                  {posterEvents.map(({ ev, poster }) => (
                    <button key={ev.id} type="button" className="home-poster-tile"
                      onClick={() => { setZoomEvent(ev); setZoom(poster); }}
                      aria-label={`${ev.name}, ${fmtWhen(ev)}. Open the poster and details`}>
                      <img src={poster.thumbData} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 'var(--gs-space-3)' }}>
                {textEvents.map(ev => (
                  <div key={ev.id} className="player-home-row">
                    <div style={{ flexGrow: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--gs-text-base)', color: 'var(--gs-text)' }}>{ev.name}</div>
                      <div className="player-home-meta">
                        {(ev.nextDate || ev.startDate) ? dayLabel(ev.nextDate || ev.startDate) : 'Date to be confirmed'}
                        {[ev.startTime, ev.cost, ev.location]
                          .map(x => (x || '').trim()).filter(Boolean)
                          .map(x => ` · ${x}`).join('')}
                      </div>
                      {ev.details && <div className="player-home-meta">{ev.details}</div>}
                    </div>
                  </div>
                ))}
              </div>
              {eventsLater.length > 0 && (
                <button type="button" className="player-home-more"
                  aria-expanded={showAllEvents}
                  onClick={() => setShowAllEvents(v => !v)}>
                  {showAllEvents
                    ? 'Show fewer'
                    : `${eventsLater.length} more, later in the year`}
                </button>
              )}
            </>
          )}

          {myEntered.length > 0 && (
            <>
              <div className="player-home-rule"></div>
              <span className="gs-section-label">You are entered in</span>
              <div style={{ marginTop: 'var(--gs-space-3)' }}>
                {myEntered.map(c => (
                  <div key={c.id} className="player-home-row">
                    <div style={{ flexGrow: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--gs-text-base)', color: 'var(--gs-text)' }}>{c.name}</div>
                      <div className="player-home-meta">{c.startDate ? dayLabel(c.startDate) : 'Date to be confirmed'}</div>
                    </div>
                    <button className="w3-button w3-round player-home-quiet" onClick={() => openComp(c)}>Open</button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── 3. how your season is going ──────────────────────────── */}
          {me && (
            <>
              <div className="player-home-rule"></div>
              <span className="gs-section-label">Your season</span>
              <div className="player-home-figures">
                <div>
                  <div className="player-home-figure">{ordinal(myIdx + 1)}</div>
                  <div className="player-home-meta">on the club ladder</div>
                </div>
                <div>
                  <div className="player-home-figure">{me.rating}</div>
                  <div className="player-home-meta">rating</div>
                </div>
                <div>
                  <div className="player-home-figure">{me.wins}&ndash;{me.losses}</div>
                  <div className="player-home-meta">won &ndash; lost, {me.played} played</div>
                </div>
                <button className="w3-button w3-round player-home-link" onClick={() => setView('clubStats')}>
                  Full leaderboard &rarr;
                </button>
              </div>
            </>
          )}

          {myLastMatch && (
            <>
              <div className="player-home-rule"></div>
              <span className="gs-section-label">Last time out</span>
              <button className="player-home-last" onClick={() => openComp(comps.find(c => c.id === myLastMatch.compId))}>
                <span style={{ fontSize: 'var(--gs-text-base)', color: 'var(--gs-text)' }}>
                  {myLastMatch.HomeTeam ? myLastMatch.HomeTeam.name : 'TBD'}
                  <span className="player-home-score"> {myLastMatch.homeScore}&ndash;{myLastMatch.awayScore} </span>
                  {myLastMatch.AwayTeam ? myLastMatch.AwayTeam.name : 'TBD'}
                </span>
                <span className="player-home-meta">{myLastMatch.compName}</span>
              </button>
            </>
          )}
        </div>

        {/* ── side rail: the things that are always true ───────────────── */}
        <div className="player-home-rail">
          {/* Who you are at this club. It used to be a name and one button,
              which did not earn the corner it sat in. The facts here are the
              ones a member actually wonders about — am I paid up, when does
              it run out, where am I on the ladder — and each is only shown
              when it is known, so the card shrinks rather than printing a
              column of dashes. */}
          <div className="player-home-card profile-card">
            <div className="profile-card-head">
              <span className="profile-card-avatar" aria-hidden="true">{initials || '?'}</span>
              <div style={{ minWidth: 0 }}>
                <div className="profile-card-name">{currentUser ? currentUser.name : ''}</div>
                <div className="profile-card-sub">{club ? club.name : ''}</div>
              </div>
            </div>

            {/* Membership type, player type, expiry. Each only when it is
                known, so a member the club has not classified yet gets a short
                card rather than a column of dashes. */}
            <dl className="profile-card-facts">
              {currentUser && currentUser.membershipType && (
                <div><dt>Membership</dt><dd>{currentUser.membershipType}</dd></div>
              )}
              {currentUser && currentUser.playerType && (
                <div><dt>Player type</dt><dd>{currentUser.playerType}</dd></div>
              )}
              {renewal.active && renewal.isLife && (
                <div><dt>Expires</dt><dd>Never — life member</dd></div>
              )}
              {renewal.active && !renewal.isLife && (
                <div>
                  {/* The same reckoning the Member ID page uses: membership
                      runs to the day before the renewal date, and once that
                      has passed the honest word is "due", not "expires". */}
                  <dt>{renewal.isExpired ? 'Renewal due' : 'Expires'}</dt>
                  <dd className={renewal.isExpired ? 'profile-card-overdue' : undefined}>
                    {dateish(renewal.date)}
                  </dd>
                </div>
              )}
              {/* Only for members who play. Having a ladder position is what
                  that means, and it says so by itself: you get one by playing
                  a competition match. No list of "playing" membership types
                  to keep in step with whatever a club decides to call them. */}
              {me && (
                <div>
                  <dt>Ladder</dt>
                  <dd>{ordinal(myIdx + 1)} of {playerStandings.length}</dd>
                </div>
              )}
            </dl>

            {/* One nudge, not the form itself — the form is on the Member ID
                page, with the membership number and the renewal date it is
                about. */}
            {needsMembershipAction && (
              <div className="profile-card-note">Your membership needs attention.</div>
            )}

            <div className="profile-card-actions">
              <button className="w3-button w3-round player-home-quiet" onClick={openMemberIdPage}>
                Show my card
              </button>
              {/* openProfile, not setView: the profile view renders whatever
                  has been fetched into it, so switching to it without fetching
                  shows an empty page rather than a person. */}
              {onOpenMyProfile && (
                <button className="w3-button w3-round player-home-quiet" onClick={onOpenMyProfile}>
                  My profile
                </button>
              )}
            </div>
          </div>

          {comingUp.length > 0 && (
            <div style={{ marginTop: 'var(--gs-space-6)' }}>
              <span className="gs-section-label">Coming up at the club</span>
              <div style={{ marginTop: 'var(--gs-space-3)' }}>
                {comingUp.map(c => (
                  <button key={c.id} className="player-home-event" onClick={() => openComp(c)}>
                    <span style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text)' }}>{c.name}</span>
                    <span className="player-home-meta">{dayLabel(c.startDate)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="player-home-rail-foot">
            <div className="player-home-rule" style={{ marginTop: 0 }}></div>
            <button className="w3-button w3-round player-home-link" onClick={() => setView('dashboard')}>
              Club overview &rarr;
            </button>
            {isAdmin && (
              <>
                <div className="player-home-meta" style={{ marginTop: 'var(--gs-space-3)' }}>
                  You are an admin at this club.
                </div>
                <button className="w3-button w3-round player-home-link" onClick={() => setView('adminUsers')}>
                  Open club admin &rarr;
                </button>
                <button className="w3-button w3-round player-home-link" onClick={() => setView('clubScreenControl')}>
                  Club screen &rarr;
                </button>
              </>
            )}
          </div>
        </div>

      </div>

      {zoom && (
        <PosterLightbox poster={zoom} onClose={() => { setZoom(null); setZoomEvent(null); }}>
          {zoomEvent && (() => {
            const facts = [zoomEvent.startTime, zoomEvent.cost, zoomEvent.location]
              .map(x => (x || '').trim()).filter(Boolean);
            return (
              <>
                <h3 className="ev-name">{zoomEvent.name}</h3>
                {zoomEvent.kind && <div className="ev-kind">{zoomEvent.kind}</div>}
                <div className="ev-when">{fmtWhen(zoomEvent)}</div>
                {facts.length > 0 && <div className="ev-facts">{facts.join(' · ')}</div>}
                {zoomEvent.details && <p className="ev-details">{zoomEvent.details}</p>}
              </>
            );
          })()}
        </PosterLightbox>
      )}
    </div>
  );
}

export default PlayerHomeView;
